/**
 * 영상 시각 대응 — 재생 시각이 영상 안 어디를 가리키는가.
 *
 * 원본이 `presentation_seconds` 를 밝혔으면 그 값이 실제 대응이고, 없으면 구간 시작에 재생
 * 시각을 더한 **합성**이다. 두 경로를 타입이 가르지만 **타일마다 말하지는 않는다** — 거의 모든
 * 원본이 대응을 밝히지 않아 같은 문장이 카메라 수만큼 반복되고, 보조 증거인 영상 열을 먹었다.
 *
 * 영상 시각은 **원본 mp4 기준**이다.
 *
 * Owns:
 * - 재생 시각 → 영상 시각과 그 근거의 종류.
 *
 * Does not own:
 * - 영상 재생 — c6 `VideoSurface`. 채널 시간 사실 — `streamTimeFacts.ts`.
 */
import {
  rowIndexAtSeconds,
  type InspectChannel,
  type InspectVideo,
} from "../domain/episodeInspect";
import type { VideoChannelSource } from "@carbon6/c6-frontend-design";

/** 도메인의 시간 대응을 공통 재생 엔진에 전달한다. 미디어 초를 공통 길이로 쓰지 않는다. */
export function inspectVideoSource(
  video: InspectVideo,
  channel: InspectChannel | undefined,
): VideoChannelSource {
  return {
    id: video.key,
    src: video.url,
    durationSeconds: video.durationSeconds > 0 ? video.durationSeconds : undefined,
    resolveTime: (seconds) => {
      const cue = videoCueAt(video, channel, seconds);
      if (cue.kind === "outside") return null;
      return { seconds: cue.videoSeconds, playback: cue.kind === "mapped" ? "frame" : "native" };
    },
  };
}

export type VideoCue =
  /** 원본이 밝힌 대응 — 이 시각의 프레임이 그 표본이다. */
  | { kind: "mapped"; videoSeconds: number }
  /** 구간 시작 + 재생 시각. 대응은 확인되지 않았다. */
  | { kind: "synthetic"; videoSeconds: number }
  /** 관측 구간 밖 — 보여 줄 프레임이 없다. */
  | { kind: "outside"; where: "before" | "after" };

/**
 * 지금 재생 시각에 이 영상이 어디를 가리키는가.
 *
 * @param video 목차가 낸 영상 선언(구간 · 크기 · codec).
 * @param channel 같은 key 의 image 채널. 없으면 합성이다.
 * @param seconds 재생 클럭의 현재 시각(episode 공통 원점 기준).
 */
export function videoCueAt(
  video: InspectVideo,
  channel: InspectChannel | undefined,
  seconds: number,
): VideoCue {
  const from = video.fromSeconds ?? 0;
  if (channel === undefined || channel.secondsFromOrigin.length === 0) {
    return { kind: "synthetic", videoSeconds: from + seconds };
  }
  const times = channel.secondsFromOrigin;
  if (seconds < times[0]) {
    return { kind: "outside", where: "before" };
  }
  if (seconds > times[times.length - 1]) {
    return { kind: "outside", where: "after" };
  }
  const index = rowIndexAtSeconds(channel, seconds);
  const presentation = channel.presentationSeconds;
  if (presentation === null) {
    // 채널의 첫 시각을 기준으로 구간 안으로 옮긴다 — 재생 0 초가 구간 시작이 아닐 수 있다.
    return { kind: "synthetic", videoSeconds: from + (seconds - times[0]) };
  }
  const videoSeconds = presentation[index];
  return videoSeconds === undefined
    ? { kind: "synthetic", videoSeconds: from + (seconds - times[0]) }
    : { kind: "mapped", videoSeconds };
}

/**
 * 타일 아래 문구 — 보여 줄 프레임이 없을 때만 말한다.
 *
 * 합성(대응 미확인)은 말하지 않는다. 그 사실은 거의 모든 원본에 해당해 카메라마다 같은 문장이
 * 서고, 정작 봐야 할 "이 시각에는 프레임이 없다" 를 가린다.
 */
export function describeCue(cue: VideoCue): string | null {
  switch (cue.kind) {
    case "mapped":
    case "synthetic":
      return null;
    case "outside":
      return cue.where === "before"
        ? "이 카메라의 관측이 시작되기 전입니다."
        : "이 카메라의 관측이 끝난 뒤입니다.";
  }
}
